import { gql, useQuery } from '@apollo/client';
import { useAtomValue } from 'jotai';
import { useMemo } from 'react';

import { Server } from '../../apollo/types';
import {
  AppointmentType,
  GetUpcomingAppointmentsQuery,
  GetUpcomingAppointmentsQueryVariables,
} from '../schedule-preview/types';
import { inSessionPatientInformationAtom } from '../state';

export const GET_IN_SESSION_APPOINTMENT = gql`
  query GetInSessionAppointment($filters: AppointmentsFilter!) {
    allAppointments(filters: $filters) {
      edges {
        node {
          appointmentShortId
          appointmentType
          timeEnd
          timeStart
        }
      }
    }
  }
`;

type InSessionAppointment = {
  appointmentShortId: string;
  appointmentType: AppointmentType;
  timeEnd: string;
  timeStart: string;
};

export function useInSessionAppointment(): InSessionAppointment | undefined {
  const inSessionPatientInfo = useAtomValue(inSessionPatientInformationAtom);
  const inSessionAppointmentShortId = inSessionPatientInfo?.inSessionAppointmentShortId;
  const patientShortId = inSessionPatientInfo?.patientShortId;

  const { data } = useQuery<GetUpcomingAppointmentsQuery, GetUpcomingAppointmentsQueryVariables>(
    GET_IN_SESSION_APPOINTMENT,
    {
      skip: !inSessionAppointmentShortId || !patientShortId,
      variables: {
        filters: {
          patientShortIds: [patientShortId!],
          canceled: false,
        },
      },
      context: { server: Server.MONOLITH },
    },
  );

  return useMemo(() => {
    if (!inSessionAppointmentShortId) {
      return undefined;
    }

    const appointment = data?.allAppointments?.edges?.find(
      (edge) => edge?.node?.appointmentShortId === inSessionAppointmentShortId,
    )?.node;

    if (!appointment?.timeEnd || !appointment.timeStart) {
      return undefined;
    }

    return {
      appointmentShortId: appointment.appointmentShortId,
      appointmentType: appointment.appointmentType,
      timeEnd: appointment.timeEnd,
      timeStart: appointment.timeStart,
    };
  }, [data?.allAppointments?.edges, inSessionAppointmentShortId]);
}
